import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  Search, ChevronLeft, ChevronRight, Loader2,
  UserCheck, UserX, Ban, CheckCircle, AlertCircle
} from 'lucide-react';
import api from '../utils/api';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUsers();
  }, [page, filter]);

  const fetchUsers = async () => {
    try {
      const response = await api.admin.getUsers({ page, limit: 20, ...(search && { search }), ...(filter && { status: filter }) });
      setUsers(response.users);
      setTotalPages(response.totalPages);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('403')) {
        navigate('/admin-portal-7x9k2m');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (userId, action) => {
    setActionLoading(userId);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.updateUser(userId, { [action]: true });
      setMessage({ type: 'success', text: `User ${action} successfully` });
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Action failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link to="/admin-portal-7x9k2m/dashboard" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Dashboard
            </Link>
            <Link to="/admin-portal-7x9k2m/reports" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Reports
            </Link>
            <Link to="/admin-portal-7x9k2m/users" className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500">
              Users
            </Link>
            <Link to="/admin-portal-7x9k2m/config" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Configuration
            </Link>
            <Link to="/admin-portal-7x9k2m/vouchers" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Vouchers
            </Link>
            <Link to="/admin-portal-7x9k2m/reviews" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Reviews
            </Link>
            <Link to="/admin-portal-7x9k2m/audit" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Audit
            </Link>
            <Link to="/admin-portal-7x9k2m/api-keys" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              API Keys
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-200' :
            'bg-red-900/50 border border-red-700 text-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="unverified">Unverified</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No users found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Subscription</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Reports</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Joined</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {users.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-700/30">
                        <td className="px-4 py-4">
                          <div className="font-medium text-white">{user.name}</div>
                          <div className="text-sm text-gray-400">{user.email}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.isEmailVerified && !user.isSuspended
                              ? 'bg-green-900/50 text-green-300'
                              : user.isSuspended
                              ? 'bg-red-900/50 text-red-300'
                              : 'bg-yellow-900/50 text-yellow-300'
                          }`}>
                            {user.isSuspended ? 'Suspended' : user.isEmailVerified ? 'Active' : 'Unverified'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300 capitalize">
                            {user.subscription?.plan || 'None'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-300">
                          {user.reportCount || 0}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-300">
                          {new Date(user.createdAt).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {actionLoading === user._id ? (
                              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                            ) : (
                              <>
                                {user.isSuspended ? (
                                  <button
                                    onClick={() => handleAction(user._id, 'unsuspend')}
                                    className="p-2 text-green-400 hover:text-green-300 transition-colors"
                                    title="Unsuspend User"
                                  >
                                    <UserCheck className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleAction(user._id, 'suspend')}
                                    className="p-2 text-yellow-400 hover:text-yellow-300 transition-colors"
                                    title="Suspend User"
                                  >
                                    <Ban className="h-4 w-4" />
                                  </button>
                                )}
                                {!user.isEmailVerified && (
                                  <button
                                    onClick={() => handleAction(user._id, 'verify')}
                                    className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                                    title="Verify Email"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;
